import { IconsProps, ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import { SearchFieldOption } from 'components/2-molecules/SearchFieldComponent/SearchFieldComponent';
import { Page } from 'context/PageContext';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { apiInstance } from '../services/Api';
import { FrontendDatasourceEvent, SearchComponent, SearchConfiguration } from './SearchConfiguration';

export class ZipcodeAddressSearch implements SearchComponent {
	searchConfiguration: SearchConfiguration;
	secondarySearchConfiguration: SearchConfiguration;
	apiSearch: boolean;
	isZipcodeSearch: boolean;
	isZipcodeByAddressSearchHack: boolean;
	minInputLength: number;
	hasHadResult: boolean;
	searchIcon: IconsProps<ThemeUnion>['icon'];
	lastValue: string;
	latency: number;
	pageContext: Partial<Page>;
	tempOptionAddressHack: SearchFieldOption;

	constructor(
		searchConfiguration: SearchConfiguration,
		pageContext: Partial<Page>,
		secondarySearchConfiguration?: SearchConfiguration
	) {
		this.searchConfiguration = searchConfiguration;
		this.apiSearch = true;
		// Hack until SPD/TIA can make fire and have a zipcode without kvh/kvhx as calculation parameter
		this.isZipcodeByAddressSearchHack =
			searchConfiguration.frontendDatasourceEvent === FrontendDatasourceEvent.ZIPCODE_BY_ADDRESS;
		this.isZipcodeSearch =
			searchConfiguration.frontendDatasourceEvent === FrontendDatasourceEvent.ZIPCODE ||
			this.isZipcodeByAddressSearchHack;
		this.minInputLength = this.isZipcodeSearch && !this.isZipcodeByAddressSearchHack ? 1 : 2;
		this.searchIcon = 'map-pin';
		this.latency = 100;
		this.pageContext = pageContext;

		// marker for adding kvhx field in handleSecondaryChange
		this.secondarySearchConfiguration = secondarySearchConfiguration ?? {};
	}

	init = async (): Promise<SearchFieldOption[]> => {
		return undefined;
	};

	handleInputChange = async (value: string): Promise<SearchFieldOption[]> => {
		if (value?.trim().length >= this.minInputLength) {
			this.lastValue = value;
			await new Promise((res) => setTimeout(res, this.latency));
			if (this.lastValue !== value) {
				// a new typing overrides this
				return;
			}

			const options: SearchFieldOption[] = [];
			try {
				const res: any = await apiInstance().get(this.searchConfiguration.url + encodeURI(value));

				res?.forEach((element) => {
					this.hasHadResult = true;
					if (this.isZipcodeSearch && !this.isZipcodeByAddressSearchHack) {
						options.push({
							label: element.tekst,
							value: element.postnummer?.nr,
						});
					} else {
						options.push({
							label: element.forslagstekst,
							value: JSON.stringify(element.data), //element.data.href,
						});
					}
				});
			} catch (error) {
				console.error(error);
			}
			return options;
		}
		return this.hasHadResult ? [] : undefined;
	};

	handleChange = async (option: SearchFieldOption): Promise<SearchFieldOption> => {
		if (!this.isZipcodeSearch || this.isZipcodeByAddressSearchHack) {
			// add kvh or kvhx from Dataforsyningen to address object
			const data = JSON.parse(option.value);
			if (data.href) {
				const resKvhx: any = await apiInstance().get(data.href);
				if (resKvhx) {
					data.kvhx = resKvhx.kvhx ?? resKvhx.kvh;
					option.value = JSON.stringify(data);
				}
			}
			if (this.isZipcodeByAddressSearchHack) {
				// HACK! keep address object in temp variable to be used in handleSecondaryChange for updating CUSTOMER_FULL_ADDRESS
				this.tempOptionAddressHack = { ...option };
				// HACK! use zipcode as value in selectedOption, but keep full address as Label
				option.value = data.postnr;
			}
		}
		return option;
	};

	handleSecondaryChange = async (
		option: SearchFieldOption,
		register: UseFormRegister<FieldValues>
	): Promise<void | string> => {
		// Dataforsyningen doesn't have kvh(x) on postal codes
		const outputFieldIds = this.searchConfiguration?.outputFields?.items?.map(
			(f) => f.content.uniqueIdentifier?.uid
		);

		let kvhx;
		try {
			kvhx = JSON.parse(option.value)?.kvhx || JSON.parse(this.tempOptionAddressHack?.value)?.kvhx;
		} catch (error) {
			// perhaps there is not always kvh(x) in Dataforsyningen address search (?)
			console.warn(error);
		}

		// add potential output fields, e.g. kvh/kvhx for zipcode search, or address for zipcode by address search, to form
		for (const outputFieldId of outputFieldIds ?? []) {
			if (outputFieldId.toUpperCase().includes('KVHX')) {
				await submitKvhx(outputFieldId, kvhx, register);
			} else {
				// all other fields will get the address, as this is a zipcode/address search
				await submitAddress(outputFieldId, this.tempOptionAddressHack, register);
			}
		}

		return kvhx ?? 'UNKNOWN_KVHX';
	};
}

const submitKvhx = async (kvhxName: string, kvhx: string, register: UseFormRegister<FieldValues>) => {
	const { onChange } = register(kvhxName, { required: true });
	await onChange({
		target: {
			name: kvhxName,
			value: {
				id: kvhxName,
				value: kvhx ?? 'UNKNOWN_KVHX',
				label: kvhx ?? 'UNKNOWN_KVHX',
				stepTitle: kvhxName,
				inputLabel: kvhxName,
				type: 'SearchFieldBlock',
			},
		},
	});
};

const submitAddress = async (addressName: string, address, register: UseFormRegister<FieldValues>) => {
	const { onChange: onChangeAddress } = register(addressName, { required: true });
	await onChangeAddress({
		target: {
			name: addressName,
			value: {
				id: addressName,
				value: address.value,
				label: address.label,
				stepTitle: 'Adresse',
				type: 'SearchFieldBlock',
			},
		},
	});
};
